import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDealer } from '../../context/DealerContext';
import { dealerApi, DealerDashboard } from '../../services/dealerApi';
import Money from '../../components/dealer/Money';
import { dealerErrorMessage, fmt } from '../../utils/dealer';

/**
 * Spec 085 (Flow J) — balance, live credit totals, open requests; the empty state offers the two ways in. With the
 * wallet switch off there is no balance (the API answers `null`) and no pack / assign entry points: per-buyer
 * requests are the only way in.
 */
export default function DealerDashboardPage() {
  const { t } = useLanguage();
  const { token, me, walletEnabled } = useDealer();
  const [data, setData] = useState<DealerDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    dealerApi.dashboard(token).then(setData).catch((err) => setError(dealerErrorMessage(err, t)));
  }, [token, t]);

  const empty = data != null && data.liveCredits === 0 && data.pendingCredits === 0 && data.openRequests === 0
    && Number(data.availableBalanceAed) === 0;

  return (
    <div className="dealer-page">
      <div className="dealer-page__header">
        <div>
          <h2>{fmt(t('dealer.dashboard.title'), { dealer: me?.dealer.tradeName ?? '' })}</h2>
          <p className="dealer-muted">{t('dealer.dashboard.subtitle')}</p>
        </div>
        <Link className="btn btn-primary" to="/dealer/bookings/new">{t('dealer.bookings.new')}</Link>
      </div>

      {error && <div className="error-message">{error}</div>}
      {!data && !error && <p className="dealer-muted">{t('dealer.common.loading')}</p>}

      {data && empty && (
        <div className="dealer-empty">
          <h3>{t('dealer.dashboard.emptyTitle')}</h3>
          {walletEnabled && <p>{t('dealer.dashboard.emptyBody')}</p>}
          <div className="dealer-actions">
            <Link className="btn btn-primary" to="/dealer/credit/request">{t('dealer.credit.requestCta')}</Link>
            {walletEnabled && (
              <Link className="btn btn-secondary" to="/dealer/credit/packs">{t('dealer.credit.buyPackCta')}</Link>
            )}
          </div>
        </div>
      )}

      {data && !empty && (
        <>
          <div className="dealer-stats">
            {walletEnabled && data.availableBalanceAed != null && (
              <div className="dealer-stat" data-testid="dealer-balance-stat">
                <span className="dealer-stat__label">{t('dealer.dashboard.balance')}</span>
                <Money className="dealer-stat__value" value={data.availableBalanceAed} />
                <Link to="/dealer/credit/assign">{t('dealer.credit.assignCta')}</Link>
              </div>
            )}
            <div className="dealer-stat">
              <span className="dealer-stat__label">{t('dealer.dashboard.liveCredits')}</span>
              <span className="dealer-stat__value">{data.liveCredits}</span>
              <span className="dealer-muted"><Money value={data.liveRemainingAed} /> {t('dealer.dashboard.remaining')}</span>
            </div>
            <div className="dealer-stat">
              <span className="dealer-stat__label">{t('dealer.dashboard.liveUsed')}</span>
              <Money className="dealer-stat__value" value={data.liveUsedAed} />
              <span className="dealer-muted">{t('dealer.dashboard.ofFace')} <Money value={data.liveFaceValueAed} /></span>
            </div>
            <div className="dealer-stat">
              <span className="dealer-stat__label">{t('dealer.dashboard.openRequests')}</span>
              <span className="dealer-stat__value">{data.openRequests}</span>
              <span className="dealer-muted">{fmt(t('dealer.dashboard.pendingCredits'), { count: data.pendingCredits })}</span>
            </div>
          </div>
          <div className="dealer-actions">
            <Link className="btn btn-secondary" to="/dealer/credit/request">{t('dealer.credit.requestCta')}</Link>
            {walletEnabled && (
              <Link className="btn btn-secondary" to="/dealer/credit/packs">{t('dealer.credit.buyPackCta')}</Link>
            )}
            <Link className="btn btn-secondary" to="/dealer/credit">{t('dealer.dashboard.viewCredit')}</Link>
          </div>
        </>
      )}
    </div>
  );
}
